// Swipe the top bar or the bottom bar sideways to change city — the live game's SwipeSwitcher (Front End/Assets/Scripts/
// SwipeSwitch), phones only. A drag on the header, the resource HUD or the bottom nav pulls a bank of clouds across the
// screen with the next (finger left) or previous (finger right) city's name on it; the next city is read while the drag
// is under way, and once the clouds cover the screen (half-way) the screen underneath becomes that city. Let go past
// half-way and the clouds blow on through; short of it and they roll back. The city list wraps round at both ends, and a
// city under attack gets dark storm clouds with lightning. Nothing of this is mounted on a mouse/trackpad device.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { PlayerVillage } from "@/api/types";
import { fetchVillage } from "@/state/queries";
import { usePrompts } from "@/state/prompts";
import { art } from "./art";
import { COVER_CLOUDS, REF_W, TOP_CLOUDS, dirOf, glide, neighbour, swipeFrame, swipePct, type CloudSpec, type SwipeDir } from "./swipeMath";
import "./citySwipe.css";

/** where a swipe may start: the header, the resource HUD, the bottom nav */
export const SWIPE_ZONES = ".hdr, .c-hud, .nav";
/** a phone or tablet: touch is the main pointer and nothing hovers */
export const PHONE_QUERY = "(hover: none) and (pointer: coarse)";
const START_PX = 12; // movement before a touch counts as a swipe (SwipeDetector.swipeThreshold, in CSS px)

/** True on a touch-first device; follows the media query if it changes (a tablet docked to a keyboard and trackpad). */
export function usePhone(): boolean {
  const mq = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(PHONE_QUERY) : null;
  const [on, setOn] = useState(() => !!mq?.matches);
  useEffect(() => {
    if (!mq) return;
    const f = () => setOn(mq.matches);
    f();
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, [mq]);
  return on;
}

type Phase = "idle" | "drag" | "ending" | "cancelling";

export interface CitySwipeProps {
  wid: number;
  /** the city on screen */
  vid: number | null;
  /** the player's cities in this world, in the game's order */
  cities: PlayerVillage[];
  /** show this city on the current screen (called at the half-way point, and again with the first city if the drag goes back) */
  onSwitch: (vid: number) => void;
}

/** Mount once per screen that supports the swipe (every city screen, the map, the guild). Renders nothing off a phone. */
export function CitySwipe(props: CitySwipeProps) {
  const phone = usePhone();
  if (!phone || props.cities.length < 2 || !props.vid) return null;
  return <SwipeLayer {...props} />;
}

function SwipeLayer({ wid, vid, cities, onSwitch }: CitySwipeProps) {
  const qc = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLImageElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const cloudEls = useRef<HTMLDivElement[]>([]);
  const spin = useMemo(() => [...COVER_CLOUDS, ...TOP_CLOUDS].map(() => (Math.random() * 2 - 1) * 5), []); // Cloud.rotationSpeed

  // the latest props, for the touch handlers and the frame loop (attached once)
  const live = useRef({ wid, vid, cities, onSwitch });
  live.current = { wid, vid, cities, onSwitch };

  useEffect(() => {
    const s = {
      phase: "idle" as Phase,
      dir: 1 as SwipeDir,
      pct: 0,
      orig: 0,
      target: 0,
      shown: 0,
      ready: new Set<number>(),
      loading: new Set<number>(),
      // the touch
      tracking: false,
      swiping: false,
      x0: 0,
      y0: 0,
      dx: 0,
      raf: 0,
      last: 0,
      angles: spin.map(() => 0),
    };
    const width = () => Math.min(window.innerWidth, document.documentElement.clientWidth || window.innerWidth);
    const main = () => document.querySelector<HTMLElement>(".shell-main");

    const prefetch = (id: number) => {
      if (s.ready.has(id) || s.loading.has(id)) return;
      s.loading.add(id);
      fetchVillage(qc, live.current.wid, id)
        .then(() => s.ready.add(id))
        .catch(() => {
          if (s.target === id && s.phase !== "idle") {
            const c = live.current.cities.find((x) => x.id === id);
            usePrompts.getState().toast(`Couldn't reach ${c?.city_name ?? "that city"}.`, "error");
            s.phase = "cancelling";
            kick();
          }
        })
        .finally(() => s.loading.delete(id));
    };

    // which city the drag is heading for, and the words on the clouds
    const prepare = () => {
      const ids = live.current.cities.map((c) => c.id);
      const t = neighbour(ids, s.orig, s.dir);
      if (t === null) return false;
      s.target = t;
      const c = live.current.cities.find((x) => x.id === t);
      const dark = !!c?.num_incoming_attacks;
      if (nameRef.current) nameRef.current.textContent = c?.city_name ?? "";
      if (labelRef.current) labelRef.current.textContent = s.dir === 1 ? "Go to next city" : "Go to previous city";
      if (arrowRef.current) arrowRef.current.src = s.dir === 1 ? art.swipe.arrowNext : art.swipe.arrowPrev;
      rootRef.current?.classList.toggle("dark", dark);
      prefetch(t);
      return true;
    };

    const apply = () => {
      const k = width() / REF_W;
      const f = swipeFrame(s.pct, s.dir);
      if (coverRef.current) coverRef.current.style.transform = `translate3d(${f.cover * k}px,0,0)`;
      if (topRef.current) topRef.current.style.transform = `translate3d(${f.top * k}px,0,0)`;
      if (helpRef.current) {
        helpRef.current.style.transform = `translate3d(${f.helpX * k}px,0,0)`;
        helpRef.current.style.opacity = String(f.helpAlpha);
      }
      if (arrowRef.current) arrowRef.current.style.transform = `rotate(${f.arrowDeg}deg)`;
      const m = main();
      if (m) m.style.transform = s.phase === "idle" ? "" : `translate3d(${f.main * k}px,0,0)`;
    };

    const frame = (now: number) => {
      s.raf = 0;
      if (s.phase === "idle") return;
      const dt = s.last ? Math.min(0.1, (now - s.last) / 1000) : 1 / 60;
      s.last = now;
      if (s.phase === "cancelling") s.pct = glide(s.pct, 0, dt);
      if (s.phase === "ending") s.pct = glide(s.pct, 1, dt);

      // the swap happens under the clouds; until the next city has loaded they hold at half-way
      if (s.pct >= 0.5) {
        if (s.ready.has(s.target)) {
          if (s.shown !== s.target) {
            s.shown = s.target;
            live.current.onSwitch(s.target);
          }
        } else s.pct = 0.5;
      } else if (s.shown !== s.orig) {
        s.shown = s.orig;
        live.current.onSwitch(s.orig);
      }

      // the storm clouds flicker with lightning (4% a frame each), and every cloud turns slowly
      const dark = rootRef.current?.classList.contains("dark");
      cloudEls.current.forEach((el, i) => {
        if (!el) return;
        s.angles[i] += spin[i] * dt;
        el.style.setProperty("--spin", `${s.angles[i]}deg`);
        if (dark) el.classList.toggle("flash", !el.classList.contains("flash") && Math.random() < 0.04);
      });

      apply();

      if ((s.phase === "cancelling" && s.pct < 0.01) || (s.phase === "ending" && s.pct > 0.99)) finish();
      else s.raf = requestAnimationFrame(frame);
    };
    const kick = () => {
      if (!s.raf) {
        s.last = 0;
        s.raf = requestAnimationFrame(frame);
      }
    };

    const finish = () => {
      s.phase = "idle";
      s.pct = 0;
      s.ready.clear();
      rootRef.current?.classList.remove("on");
      cloudEls.current.forEach((el) => el?.classList.remove("flash"));
      const m = main();
      if (m) m.style.transform = "";
    };

    // ------------------------------------------------------------ the touch
    const begin = () => {
      const cur = live.current.vid;
      if (!cur) return false;
      s.orig = cur;
      s.shown = cur;
      s.dir = dirOf(s.dx);
      if (!prepare()) return false;
      s.phase = "drag";
      s.pct = swipePct(s.dx, width());
      rootRef.current?.classList.add("on");
      kick();
      return true;
    };

    const onStart = (e: TouchEvent) => {
      if (s.phase !== "idle" || e.touches.length !== 1) {
        s.tracking = false;
        return;
      }
      const t = e.target as Element | null;
      if (!t || !t.closest?.(SWIPE_ZONES) || t.closest(".scrim")) return;
      s.tracking = true;
      s.swiping = false;
      s.x0 = e.touches[0].clientX;
      s.y0 = e.touches[0].clientY;
      s.dx = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!s.tracking || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - s.x0;
      const dy = e.touches[0].clientY - s.y0;
      s.dx = dx;
      if (!s.swiping) {
        if (Math.abs(dy) > START_PX && Math.abs(dy) >= Math.abs(dx)) {
          s.tracking = false; // a vertical drag: leave it to the page
          return;
        }
        if (Math.abs(dx) <= START_PX) return;
        if (!begin()) {
          s.tracking = false;
          return;
        }
        s.swiping = true;
      }
      if (e.cancelable) e.preventDefault();
      if (s.phase !== "drag") return;
      const d = dirOf(dx);
      if (d !== s.dir) {
        s.dir = d;
        prepare();
      }
      s.pct = swipePct(dx, width());
      kick();
    };

    const onEnd = () => {
      if (!s.tracking) return;
      s.tracking = false;
      if (!s.swiping) return;
      s.swiping = false;
      // the finger was on a button or a nav link: the swipe isn't a tap on it
      const eat = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        window.removeEventListener("click", eat, true);
      };
      window.addEventListener("click", eat, true);
      window.setTimeout(() => window.removeEventListener("click", eat, true), 500);
      if (s.phase !== "drag") return;
      s.phase = s.pct >= 0.5 ? "ending" : "cancelling";
      kick();
    };

    document.addEventListener("touchstart", onStart, { passive: true, capture: true });
    document.addEventListener("touchmove", onMove, { passive: false, capture: true });
    document.addEventListener("touchend", onEnd, { capture: true });
    document.addEventListener("touchcancel", onEnd, { capture: true });
    return () => {
      document.removeEventListener("touchstart", onStart, true);
      document.removeEventListener("touchmove", onMove, true);
      document.removeEventListener("touchend", onEnd, true);
      document.removeEventListener("touchcancel", onEnd, true);
      if (s.raf) cancelAnimationFrame(s.raf);
      const m = main();
      if (m) m.style.transform = "";
    };
  }, [qc, spin]);

  let n = 0;
  const cloud = (c: CloudSpec, i: number) => {
    const idx = n++;
    return (
      <div
        key={i}
        className="cs-cloud"
        ref={(el) => { if (el) cloudEls.current[idx] = el; }}
        style={{ "--x": c.x, "--y": -c.y, "--w": c.w, "--h": c.h, "--rot": `${c.deg}deg` } as CSSProperties}
      >
        <img className="clear" src={art.swipe.cloud} alt="" draggable={false} />
        <img className="storm" src={art.swipe.cloudDark} alt="" draggable={false} />
        <img className="bolt" src={art.swipe.cloudLightning} alt="" draggable={false} />
      </div>
    );
  };

  return createPortal(
    <div className="cswipe" ref={rootRef} aria-hidden="true" data-testid="city-swipe">
      <div className="cs-layer" ref={coverRef}>{COVER_CLOUDS.map(cloud)}</div>
      <div className="cs-layer" ref={topRef}>{TOP_CLOUDS.map(cloud)}</div>
      <div className="cs-help" ref={helpRef}>
        <div className="cs-name" ref={nameRef} />
        <div className="cs-arrowbox"><img className="cs-arrow" ref={arrowRef} src={art.swipe.arrowNext} alt="" draggable={false} /></div>
        <div className="cs-label" ref={labelRef} />
      </div>
    </div>,
    document.body,
  );
}
